import { AnyFood, ComplexFood, isLogMealById } from '@cf-shared/data-model/food';
import { Log_Meal } from '@cf-shared/data-model/log-meal';
import { MealTunerMeal } from '@cf-shared/meal-tuner-interface';

/**
 * Which meals the meal-suggestions page offers, once the meal tuner has scored them.
 *
 * The page gathers candidates (the user's recipes plus the meals they logged recently),
 * asks the tuner how well each fits the remaining targets, and shows a short list. Two
 * rules decide that list, and they live here rather than inside the page so they can be
 * unit-tested: nothing in an Angular page class is reachable from a unit test in this repo.
 *
 * RULE 1 — a recipe beats a logged meal with the same ingredients. A logged meal is often
 * just a recipe the user ate, so showing both would list the same food twice. Two meals are
 * "the same" when their ingredient fingerprints match (the fingerprint ignores amounts).
 * Every recipe's fingerprint is registered first, so a recipe always wins whatever order the
 * candidates arrive in. Among logged meals, the first one with a given fingerprint wins.
 *
 * RULE 2 — a recent logged meal that fits very well is added even when it did not make the
 * top of the list. The tuner's top results are shown first. After them come the logged
 * meals that fit at least `kRecentMealBoostMinMatchPercent`, unless the list already shows
 * that meal, or already shows the recipe it was based on (directly, or through a logged
 * meal in the top results that was based on the same recipe).
 *
 * RULE 3 — a suggestion whose food cannot be read is left out and reported, instead of
 * making the page give up on the whole list. See `readSuggestionFoodsAsync`.
 */

/** How many of the tuner's best results are always shown. */
export const kTopSuggestionCount = 10;

/** The tuner match, in percent, a recent logged meal needs to be added below the top results. */
export const kRecentMealBoostMinMatchPercent = 90;

/**
 * Rule 1: drop every logged meal whose ingredients already appear as a recipe or as an
 * earlier logged meal. Candidates that stay keep their original order.
 */
export function dedupeCandidatesByFingerprint(
  candidates: AnyFood[],
  fingerprintOf: (food: ComplexFood) => string
): AnyFood[] {
  const seenFingerprints = new Set<string>();
  // first pass: register fingerprints for recipes (non-Log_Meals), so recipes always win
  for (const candidate of candidates) {
    if (!isLogMealById(candidate._id)) {
      seenFingerprints.add(fingerprintOf(candidate as ComplexFood));
    }
  }
  // second pass: remove Log_Meals whose ingredients already appear (as a recipe or earlier Log_Meal)
  return candidates.filter(candidate => {
    if (isLogMealById(candidate._id)) {
      const fp = fingerprintOf(candidate as ComplexFood);
      if (seenFingerprints.has(fp)) return false;
      seenFingerprints.add(fp);
    }
    return true;
  });
}

/**
 * Rule 2: the tuner's top results, followed by the well-fitting recent logged meals that
 * are not already represented. `tunerMeals` is the tuner's ranked answer; `candidates` is
 * the list that was handed to the tuner.
 */
export function selectSuggestedMeals(tunerMeals: MealTunerMeal[], candidates: AnyFood[]): MealTunerMeal[] {
  const topResults = tunerMeals.slice(0, kTopSuggestionCount);

  const representedIds = new Set<string>();
  for (const meal of topResults) {
    representedIds.add(meal.id);
  }
  // also count the recipe a top-result Log_Meal was based on as represented
  for (const meal of topResults) {
    if (isLogMealById(meal.id)) {
      const logMeal = candidates.find(c => c._id === meal.id) as Log_Meal;
      if (logMeal?.basedOnFoodId) {
        representedIds.add(logMeal.basedOnFoodId);
      }
    }
  }

  const boostedMeals: MealTunerMeal[] = [];
  const recentMeals = candidates.filter(c => isLogMealById(c._id)) as Log_Meal[];
  for (const logMeal of recentMeals) {
    // skip if already in top results
    if (representedIds.has(logMeal._id)) continue;
    // skip if its base recipe is already represented
    if (logMeal.basedOnFoodId && representedIds.has(logMeal.basedOnFoodId)) continue;
    // if the tuner scored this meal well enough, add it
    const tunerResult = tunerMeals.find(m => m.id === logMeal._id);
    if (tunerResult && tunerResult.precision_pc >= kRecentMealBoostMinMatchPercent) {
      boostedMeals.push(tunerResult);
    }
  }

  return [...topResults, ...boostedMeals];
}

/** One suggestion the page can show: the tuner's answer for it, and the food it is about. */
export interface ReadableSuggestion {
  meal: MealTunerMeal;
  food: AnyFood;
}

/**
 * Rule 3: read the food behind each suggestion, and leave out any whose food did not come back.
 *
 * A read answers nothing when the food no longer exists, or when it could not be read at all
 * (df-721047a6faf6). Building a row from that nothing used to throw, and the throw took the
 * whole page with it: the user was sent back and told the suggestion service was unavailable.
 * One unreadable suggestion now costs only its own row. Each one left out is reported, because
 * a food the user's own candidate list pointed at should have been readable.
 *
 * The suggestions that remain keep their order.
 */
export async function readSuggestionFoodsAsync(
  meals: MealTunerMeal[],
  readFoodAsync: (id: string) => Promise<AnyFood | null | undefined>,
  logging: { trackSilentProblem(problemName: string, context: any): void },
): Promise<ReadableSuggestion[]> {
  const readable: ReadableSuggestion[] = [];
  for (const meal of meals) {
    const food = await readFoodAsync(meal.id);
    // if nothing came back, report it and move on to the next suggestion
    if (!food) {
      logging.trackSilentProblem('meal_suggestion_food_unreadable', {
        id: meal.id,
        kind: isLogMealById(meal.id) ? 'log-meal' : 'food',
      });
      continue;
    }
    readable.push({ meal, food });
  }
  return readable;
}
